import type { JourneyTimingEvent, JourneyTimingObserver } from '@adapters/telemetry/JourneyTimingObserver';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';

export const journeyTimingEmissionFailureEvent = 'inav:journey-timing-emission-failure';

export type JourneyTimingEmissionFailureReason =
  | 'invalid-envelope'
  | 'unauthorized'
  | 'endpoint-unavailable'
  | 'unexpected-status'
  | 'transport-error';

export type JourneyTimingEmissionFailureDetail = Readonly<{
  reason: JourneyTimingEmissionFailureReason;
  statusCode?: number;
}>;

export class BffJourneyTimingObserver implements JourneyTimingObserver {
  private static readonly endpointPath = '/api/telemetry/journey-timing';

  public constructor(private readonly transport = new AuthenticatedBffTransport()) {}

  public record(event: JourneyTimingEvent): void {
    void this.transport.fetch(BffJourneyTimingObserver.endpointPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) {
          this.reportEmissionFailure(this.classifyEmissionFailure(response.status));
        }
      })
      .catch(() => {
        this.reportEmissionFailure({ reason: 'transport-error' });
      });
  }

  private classifyEmissionFailure(statusCode: number): JourneyTimingEmissionFailureDetail {
    if (statusCode === 400) {
      return { reason: 'invalid-envelope', statusCode };
    }

    if (statusCode === 401 || statusCode === 403) {
      return { reason: 'unauthorized', statusCode };
    }

    if (statusCode >= 500) {
      return { reason: 'endpoint-unavailable', statusCode };
    }

    return { reason: 'unexpected-status', statusCode };
  }

  private reportEmissionFailure(detail: JourneyTimingEmissionFailureDetail): void {
    globalThis.dispatchEvent(new CustomEvent<JourneyTimingEmissionFailureDetail>(journeyTimingEmissionFailureEvent, {
      detail,
    }));
  }
}